import { useGlobalStore } from '@modules/core/store';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import useGetCredits from '@modules/core/hooks/queries/useGetCredits';
import useGetCreditsPriceList from '@modules/core/hooks/queries/useGetCreditsPriceList';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import { toast } from '@huspy/forge/shared';
import { AMPLITUDE_FEATURE_FLAGS, CreditsPriceListActionName } from '@modules/core/api/types';

const LOW_CREDITS_ERROR = new Error('low credits');

const calculateCreditBalance = (availableCredits: number, creditsToCharge: number) => availableCredits + creditsToCharge;

// eslint-disable-next-line sonarjs/cognitive-complexity
const useCredits = () => {
  const { user } = usePersistUser();
  const { setIsOpenedBuyCredits, setBuyCreditsToSpend } = useGlobalStore();
  const { featureFlags, isFeatureFlagsLoading } = useFeatureFlags();

  const isCreditsModuleAvailableByFF = !isFeatureFlagsLoading
  && featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREDITS_MODULE];

  const isLowCreditsAvailableByFF = !isFeatureFlagsLoading
  && isCreditsModuleAvailableByFF && featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_LOW_CREDITS_FEATURE_BLOCKING];

  const { getActionPrice } = useGetCreditsPriceList(isCreditsModuleAvailableByFF);
  const { data: creditsData, isLoading, refetch } = useGetCredits(
    user?.brokerageId,
    isCreditsModuleAvailableByFF
  );

  const isEnoughCredits = (creditsToCharge: number) => {
    if (creditsData?.available_credits === undefined) {
      return false;
    }

    return calculateCreditBalance(Number(creditsData?.available_credits ?? 0), creditsToCharge) >= 0;
  };

  const callCreditsAction = async (
    actionNameOrNumberOfCredits: CreditsPriceListActionName | number,
    successCallback?: () => void,
    failCallback?: (error: Error) => void
  ) => {
    if (IS_SPAIN_ENV || !isLowCreditsAvailableByFF) {
      successCallback?.();
      return;
    }

    const creditsToCharge = typeof actionNameOrNumberOfCredits === 'string'
      ? getActionPrice(actionNameOrNumberOfCredits)
      : actionNameOrNumberOfCredits;

    const { data } = await refetch();

    if (creditsToCharge === undefined || data?.available_credits === undefined) {
      toast('info', { message: 'Credits not available. Please try again' });
      return;
    }

    if (calculateCreditBalance(Number(data?.available_credits ?? 0), creditsToCharge) >= 0) {
      successCallback?.();
      return;
    }

    setBuyCreditsToSpend(creditsToCharge);
    setIsOpenedBuyCredits(true);
    failCallback?.(LOW_CREDITS_ERROR);
  };

  return {
    data: creditsData ? { availableCredits: creditsData?.available_credits } : null,
    isEnoughCredits,
    callCreditsAction,
    isLoading,
    refetch,
  };
};

export default useCredits;
